// src/services/baristaService.js
// Service for barista operations and order management

import { formatUTCDateLocale } from '../utils/dateUtils'

import apiClient from './api'
import AppURLs from '../config/urls'

class BaristaService {
  /**
   * Get all barista orders with filters
   * @param {Object} params - Query parameters
   * @param {string} params.branchId - Branch ID
   * @param {string} params.status - Order status (pending, preparing, completed)
   * @param {string} params.orderType - Order type (dine_in, take_away, delivery)
   * @param {string} params.orderId - Specific order ID
   * @param {string} params.tableId - Table ID
   * @param {string} params.dateFrom - Start date
   * @param {string} params.dateTo - End date
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.sortBy - Sort field
   * @param {string} params.sortOrder - Sort order (asc, desc)
   * @returns {Promise<Object>} API response with barista orders
   */
  async getBaristaOrders(params = {}) {
    try {
      console.log('🔍 Fetching barista orders with params:', params)
      
      // Clean up undefined values
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== '')
      )
      
      const response = await apiClient.get(AppURLs.BARISTA_ORDERS.GET_ALL, cleanParams)
      
      return response
    } catch (error) {
      console.error('❌ Error fetching barista orders:', error)
      throw error
    }
  }

  /**
   * Get a specific barista order by ID
   * @param {string} orderId - Barista order ID
   * @returns {Promise<Object>} API response with barista order details
   */
  async getBaristaOrderById(orderId) {
    try {
      console.log('🔍 Fetching barista order by ID:', orderId)
      
      const response = await apiClient.get(AppURLs.BARISTA_ORDERS.GET_BY_ID.replace(':id', orderId))
      
      console.log('✅ Barista order fetched successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('❌ Error fetching barista order:', error)
      throw error
    }
  }

  /**
   * Update barista order status
   * @param {string} orderId - Barista order ID
   * @param {string} status - New status (pending, preparing, completed)
   * @returns {Promise<Object>} API response
   */
  async updateBaristaOrderStatus(orderId, status) {
    try {
      console.log(`🔄 Updating barista order ${orderId} status to:`, status)
      
      const response = await apiClient.patch(AppURLs.BARISTA_ORDERS.UPDATE_STATUS.replace(':id', orderId), {
        status
      })
      
      console.log('✅ Barista order status updated successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('❌ Error updating barista order status:', error)
      throw error
    }
  }

  /**
   * Get order type display name
   * @param {string} orderType - API order type
   * @returns {string} Display name
   */
  getOrderTypeDisplayName(orderType) {
    const typeMap = {
      'dine_in': 'Dine-in',
      'take_away': 'Take-away', 
      'takeaway': 'Take-away',
      'delivery': 'Delivery'
    }
    return typeMap[orderType] || 'Unknown'
  }

  /**
   * Get status display name and color
   * @param {string} status - Order status
   * @returns {Object} Display info with name and color
   */
  getStatusDisplayInfo(status) {
    const statusMap = {
      'pending': {
        name: 'Pending',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        badgeColor: 'bg-yellow-500'
      },
      'preparing': {
        name: 'Preparing', 
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        badgeColor: 'bg-blue-500'
      },
      'completed': {
        name: 'Completed',
        color: 'bg-green-100 text-green-800 border-green-200', 
        badgeColor: 'bg-green-500'
      }
    }
    return statusMap[status] || {
      name: 'Unknown',
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      badgeColor: 'bg-gray-500'
    }
  }

  /**
   * Format currency amount
   * @param {number} amount - Amount to format
   * @returns {string} Formatted currency string
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  /**
   * Format date/time for display
   * @param {string|Date} date - Date to format
   * @returns {string} Formatted date string
   */
  formatDateTime(date) {
    return formatUTCDateLocale(date, {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  /**
   * Calculate total items in order
   * @param {Array} orderItems - Array of order items
   * @returns {number} Total item count
   */
  calculateTotalItems(orderItems = []) {
    return orderItems.reduce((total, item) => total + (item.quantity || 0), 0)
  }

  /**
   * Get status button configuration
   * @param {string} currentStatus - Current order status
   * @returns {Object|null} Button configuration or null if no action available
   */
  getStatusButtonConfig(currentStatus) {
    switch (currentStatus) {
      case 'pending':
        return {
          text: 'Start Preparing',
          nextStatus: 'preparing',
          className: 'bg-blue-600 hover:bg-blue-700 text-white',
          action: 'status_change'
        }
      case 'preparing':
        return {
          text: 'Mark Complete',
          nextStatus: 'completed', 
          className: 'bg-green-600 hover:bg-green-700 text-white',
          action: 'status_change'
        }
      case 'completed':
        return {
          text: 'Print Bill',
          action: 'print',
          className: 'bg-orange-600 hover:bg-orange-700 text-white'
        }
      default:
        return null
    }
  }
}

export default new BaristaService()
